import React, { useState, useEffect, useRef } from "react";
import { InputAdornment, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./SearchV2.css";
import { customSxV2 } from "../createSecondaryOrder/util";

interface SearchV2Props<T> {
  searchFunction: (value: string) => void;
  label?: string;
  resetSearch?: boolean;
  value?: string;
}

const SearchV2 = <T extends Record<string, any>>({
  searchFunction,
  label = "Search",
  resetSearch = false,
  value = ""
}: SearchV2Props<T>) => {
  const [searchValue, setSearchValue] = useState(value);
  const [debouncedValue, setDebouncedValue] = useState(searchValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = () => {
    setSearchValue("");
    setDebouncedValue("");
    searchFunction("");
  };

  // Focus on Ctrl+F or Cmd+F
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset search value when resetSearch changes
  useEffect(() => {
    if (resetSearch) {
      handleClear();
    }
  }, [resetSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchValue);
    }, 400);

    return () => {
      clearTimeout(timer); 
    };
  }, [searchValue]);

  useEffect(() => {
    setSearchValue(value);
    setDebouncedValue(value);
  }, [value]);

  useEffect(() => {
    searchFunction(debouncedValue);
  }, [debouncedValue, searchFunction]);

  return (
    <TextField
    id="outlined-basic"
    placeholder={label}
    variant="outlined"
    size="small"
    autoComplete="off"
    value={searchValue}
    onChange={(e) => setSearchValue(e.target.value)}
    sx={{...customSxV2,
        maxWidth: 250
      }}
    inputRef={inputRef}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="SearchV2_icon"/>
        </InputAdornment>
      ),
      endAdornment: searchValue && (
        <InputAdornment position="end">
          <FontAwesomeIcon
            icon={faTimes}
            onClick={handleClear}
            style={{ cursor: "pointer" }}
          />
        </InputAdornment>
      ),
    }}
  />
  );
};

export default SearchV2;
